// A meeting's AI-suggested links (MOTHER-204), over the routes of MOTHER-198 and MOTHER-199:
// GET /api/meetings/:id/link-suggestions lists the ones still waiting for a person, and
// POST /api/meetings/:id/link-suggestions/:suggestionId { decision } accepts or dismisses one.
// The meeting page shows them with Foundation's SuggestedLinksSection.
import type { SuggestedLink, SuggestedLinkKind } from '@olytic/foundation';

import { authenticatedFetch } from '../auth/authClient';
import { recordHref } from '../objects/routes';

/** One suggestion as the GET route answers it. */
export interface LinkSuggestion {
  suggestion_id: string;
  kind: SuggestedLinkKind;
  record_id: string;
  record_name: string | null;
  role: 'attendee' | 'mentioned' | null;
  confidence: number;
  evidence: string | null;
  created_at: string | null;
}

export interface LinkSuggestions {
  meeting_id: string;
  total_suggestions: number;
  suggestions: LinkSuggestion[];
}

export type LinkDecision = 'accept' | 'dismiss';

/** A refused request: the server's `error` text, and the status (409 is "Already decided."). */
export class LinkSuggestionError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'LinkSuggestionError';
  }
}

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

const suggestionsUrl = (meetingId: string) => `/api/meetings/${encodeURIComponent(meetingId)}/link-suggestions`;

/** The body as JSON, or a LinkSuggestionError carrying the server's `error` text when it refused. */
async function answer<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => null)) as (T & { error?: unknown }) | null;
  if (!res.ok || !body) {
    const error = typeof body?.error === 'string' && body.error.trim() !== '' ? body.error : `Something went wrong (${res.status}).`;
    throw new LinkSuggestionError(res.status, error);
  }
  return body;
}

/** The meeting's suggestions still waiting for a person, surest first as the server sends them. */
export async function loadLinkSuggestions(meetingId: string, fetcher: Fetcher = authenticatedFetch): Promise<LinkSuggestions> {
  const body = await answer<LinkSuggestions>(await fetcher(suggestionsUrl(meetingId)));
  return { ...body, suggestions: Array.isArray(body.suggestions) ? body.suggestions : [] };
}

/** Accepts or dismisses one suggestion. Answers its new status; a refusal throws LinkSuggestionError. */
export async function decideLinkSuggestion(
  meetingId: string,
  suggestionId: string,
  decision: LinkDecision,
  fetcher: Fetcher = authenticatedFetch,
): Promise<{ suggestion_id: string; status: 'accepted' | 'dismissed' }> {
  return answer(
    await fetcher(`${suggestionsUrl(meetingId)}/${encodeURIComponent(suggestionId)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ decision }),
    }),
  );
}

/** Each suggestion's kind, and the object whose page it links to. */
const OBJECT_OF: Record<SuggestedLinkKind, string> = {
  account: 'accounts',
  opportunity: 'opportunities',
  project: 'projects',
  contact: 'contacts',
};

/** A suggestion in SuggestedLinksSection's shape, its name linking to the record's page. */
export function toSuggestedLink(s: LinkSuggestion): SuggestedLink {
  return {
    id: s.suggestion_id,
    kind: s.kind,
    name: s.record_name ?? 'Unnamed',
    role: s.role,
    confidence: s.confidence,
    evidence: s.evidence,
    href: recordHref(OBJECT_OF[s.kind], s.record_id),
  };
}
